/****************************************************
 * Module Name:  addPackageDetail.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import Modal from "react-bootstrap/Modal";
import Constants from "../../utility/constants";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import "./../../style/modalstyle.css";
import Utility from "../../utility/utility";
import CommonSnackbar from "../common/commonSnackBar";
import PacketHistoryModal from "../common/packetHistoryModal";
import { LoadingButton } from "@mui/lab";
import PrintBarcode from "./printBarcode";
import { IoMdBarcode } from "react-icons/io";
import logo from '../../images/pansuriya.jpg';
import { DataGridPro, GridToolbar, GridEditInputCell, } from "@mui/x-data-grid-pro";
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PacketProxy from "../../utility/proxy/manager/manufacturingProxy/packetProxy";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { BackupTable } from "@mui/icons-material";
import Papa from 'papaparse';

const AddPackageDetail = ({
    handleClose,
    Show,
    loading,
    handleSave,
    handleValidation,
    sourceData,
    fromKapan,
    isKapanStatusNew, // added for further packaging (might need)
    kapanId,
    fromPacketId,
    addPacketPermission,
    totalPacketCount,
    isPacketStatusNew,
    kapanppc,
    kapanPcs,
    maxCarats
}) => {
    const { apiRef, handleCellClick } = Utility.useSingleClickEdit();
    const [data, setData] = useState([]);
    const [packetId, setPacketId] = useState("");
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);
    const [showError, setShowError] = useState(false);
    const [barcodeModal, setBarcodeModal] = useState(false);
    const [detailLoader, setDetailLoader] = useState(false);
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [maxPacketSequenceID, setMaxPacketSequenceID] = useState(""); //storing as not to call api again
    const [currentLastPacketSequenceID, setCurrentLastPacketSequenceID] = useState("");

    const [uploadLoader, setUploadLoader] = useState(false);

    useEffect(() => {
        preProcessPacketIdData();
    }, []);

    const preProcessPacketIdData = () => {
        let packetSeqID = "";

        // If either "KAPAN" or "PACKET" is "NEW",THEN PACKET WILL START FROM 'A' OR '1'.
        if (isKapanStatusNew || isPacketStatusNew) {
            if (fromKapan) {
                packetSeqID = "1";  // IF THIS IS CALLED FROM KAPAN TO PACKET
            } else {
                packetSeqID = "A"; // IF THIS IS CALLED FROM PACKET TO SUB-PACKET
            }

            // NEED TO MAINTAIN FOR FURTHER INCREMENTING
            setCurrentLastPacketSequenceID(packetSeqID);
            setMaxPacketSequenceID(packetSeqID); //storing as not to call api again

            // COMMON METHOD TO ADD ROW BASED ON  'INCREMENTATION KEY' & 'NO OF ROWS'
            processRows(packetSeqID, totalPacketCount);

            setDetailLoader(false);
            return;
        } else {
            setDetailLoader(true);

            if (fromKapan) {
                PacketProxy.GetMaxPacketIdFromKapanId(kapanId, (response) => {
                    if (response) {
                        let maxPacketID = response[0];

                        // Increment the numeric packet sequence ID.
                        packetSeqID = (parseInt(maxPacketID, 10) + 1).toString();

                        // NEED TO MAINTAIN FOR FURTHER INCREMENTING
                        setCurrentLastPacketSequenceID(packetSeqID);
                        setMaxPacketSequenceID(packetSeqID); //storing as not to call api again

                        // TO PROCESS EXISTING DATA OF THAT KAPAN/PACKET
                        processSourceData();
                    }
                    setDetailLoader(false);
                });
            } else {
                PacketProxy.GetMaxPacketIdFromKapanIdAndPacketId(kapanId, fromPacketId, (response) => {
                    if (response) {
                        let maxPacketID = response[0];

                        // Increment the 'ALPHA-NUMERIC' packet sequence ID.
                        packetSeqID = String.fromCharCode(maxPacketID.charCodeAt(maxPacketID.length - 1) + 1);

                        // NEED TO MAINTAIN FOR FURTHER INCREMENTING
                        setCurrentLastPacketSequenceID(packetSeqID);
                        setMaxPacketSequenceID(packetSeqID);  //storing as not to call api again

                        // TO PROCESS EXISTING DATA OF THAT KAPAN/PACKET
                        processSourceData();
                    }
                    setDetailLoader(false);
                });
            }
        }
    };

    const processRows = (packetSeqID, packetsCount) => {
        setDetailLoader(true);

        if (packetsCount) {
            let rows = [];

            for (let i = 1; i <= packetsCount; i++) {

                rows.push({
                    "packetid": !isNaN(packetSeqID) ? packetSeqID : (fromPacketId + packetSeqID),  //if its sub-packaging then need to append the packetid as well 
                    "carats": null,
                    "ppc": kapanppc,
                    "isnew": true, // for delete icon
                });

                if (!isNaN(packetSeqID)) {
                    packetSeqID = (parseInt(packetSeqID, 10) + 1).toString(); // numeric key incrementation

                } else {
                    packetSeqID = String.fromCharCode(packetSeqID.charCodeAt(packetSeqID.length - 1) + 1); // alpha-numeric key incrementation
                }
            }
            setCurrentLastPacketSequenceID(packetSeqID); // to update the last incremented sequence

            let finalrows = [...data];
            finalrows = finalrows.concat(rows);

            setData(finalrows);
            setDetailLoader(false);
        }
    }

    const processSourceData = () => {
        let rows = [];

        sourceData.forEach(iData => {
            let irow = {
                "packetid": iData.packetid,
                "carats": iData.carats,
                "ppc": iData.price || iData.ppc,
                "isnew": false // no delete icon if record is already created
            }
            rows.push(irow);
        });
        setData(rows);
    }

    const addPacket = () => {

        // IF CALLED FROM PACKET TO SUB-PACKET THEN CANNOT EXCEED ROWS AFTER 'Z'
        if (!fromKapan && data.length >= 26) {
            setSnackbarState(true, `Packets cannot be more than 26`, "warning");
        } else {

            // COMMON METHOD TO ADD ROW BASED ON  'INCREMENTATION KEY' & 'NO OF ROWS'
            processRows(currentLastPacketSequenceID, 1)
        }
    }

    const deletePacket = (packetid) => {

        let newData = data.filter((item) => item.packetid !== packetid);

        if (newData.length === 0) {
            setData(newData);
            setCurrentLastPacketSequenceID(maxPacketSequenceID); // setting it back to what we got from api
            return;
        };

        let seqId = maxPacketSequenceID;  // need to change the packetids for all remaining new creted records

        // method that increments the id
        const getNextSeqId = (id) => {
            return !isNaN(id) ? (parseInt(id, 10) + 1).toString() :
                String.fromCharCode(id.charCodeAt(id.length - 1) + 1);
        };

        //update and set for new records only
        const updatedData = newData.map(item =>
            item.isnew ? {
                ...item, packetid: !isNaN(seqId) ? seqId : (fromPacketId + seqId)
                , seqId: (seqId = getNextSeqId(seqId))
            } : item
        );

        setCurrentLastPacketSequenceID(seqId);
        setData(updatedData);
    }

    const handleCreateLot = () => {
        let totalCarats = 0;
        rows.filter(iData => iData.isnew === true).forEach(iData => {
            if (iData.carats) totalCarats += parseFloat(iData.carats);
        });
        if (parseFloat(totalCarats) > parseFloat(maxCarats)) {
            setSnackbarState(true, "Total Carats cannot be greater than " + maxCarats + " carats", "warning");
            return;
        }
        let reqdata = data;
        if (handleSave) {
            if (handleValidation) {
                if (handleValidation(reqdata)) {
                    handleSave(reqdata);
                } else {
                    setShowError(true);
                }
            } else {
                handleSave(reqdata);
            }
        }
    };

    const getChangedFields = (newRow, oldRow) => {
        const changedFields = [];
        Object.keys(newRow).forEach(field => {
            if (newRow[field] !== oldRow[field]) {
                changedFields.push({
                    id: newRow.packetid,
                    field: field,
                    value: newRow[field]
                });
            }
        });
        return changedFields;
    };

    const handleProcessRowUpdateError = (error) => {
        // console.error(error);
    };

    const processRowUpdate = (newR, oldRow) => {
        const changedFields = getChangedFields(newR, oldRow);

        if (changedFields.length === 0) return { ...newR };

        const { id, field, value } = changedFields[0];

        let newRecord = null;
        const updatedRow = data.map((iRow) => {
            if (iRow.packetid !== id) return iRow;
            if (Utility.CheckisNumberGreaterThanZero(value)) {
                if (field === "carats") {
                    const carats = value ? parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS)) : 0;

                    newRecord = { ...iRow, [field]: carats };
                    return newRecord;
                }
                else if (field === "ppc") {
                    const ppc = value ? parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS)) : 0;

                    newRecord = { ...iRow, [field]: ppc };
                    return newRecord;
                }
            } else {
                setSnackbarState(true, `Value cannot be negative or 0`, "warning");
            }
            return iRow;
        });

        if (newRecord)
            setData(updatedRow);
        return newRecord || oldRow;
    };

    const setSnackbarState = (show, message, variant) => {
        setShowSnackbar(show);
        setSnackbarMessage(message);
        setSnackbarVariant(variant);
    };

    const columnName = [
        {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: (params) => (Utility.NeedToShowPacketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handlePacketDetailModal(params.value)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "carats",
            headerName: "ENTER CTS",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            editable: true,
            hideable: false,
            width: 150,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value}
                    </span>
                    <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />
                </div>
            ),
            renderEditCell: (params) => {
                return (<Box ><GridEditInputCell {...params} /></Box>)
            },
            cellClassName: 'lavenderCell',
        },
        {
            field: "ppc",
            headerName: "ENTER PPC",
            headerAlign: "center",
            type: "number",
            editable: true,
            hideable: false,
            width: 150,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value ? parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS) : 0.00}
                    </span>
                    <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />
                </div>
            ),
            renderEditCell: (params) => {
                return (<Box ><GridEditInputCell {...params} /></Box>)
            },
            cellClassName: 'lavenderCell',
        },
        {
            field: "delete",
            headerName: "",
            headerAlign: "center",
            align: "center",
            minWidth: 60,
            flex: 1,
            renderCell: (params) => params.row.isnew ? (
                <DeleteForeverIcon
                    color="error"
                    onClick={(e) => deletePacket(params.row.packetid)}
                    sx={{ cursor: "pointer" }}
                />
            ) : params.row.isnew,
            disableExport: true,
        },
    ];

    const handlePacketDetailModal = (params) => {
        setPacketId(params);
        setShowPacketDetailModal(true);
    };

    const close = () => {
        setShowPacketDetailModal(false)
    }

    const handleUpload = (file) => {
        setUploadLoader(true);
        const reader = new FileReader();
        reader.onload = () => {
            const text = reader.result;
            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                complete: (result) => {
                    if (result.data.length <= 0) {
                        setSnackbarState(true, "No Packets Found", "warning");
                        return;
                    }

                    let headersFromCSV = Object.keys(result.data[0]);
                    let requiredColumns = ['packetid', 'carats', 'ppc'];
                    if (headersFromCSV.length === requiredColumns.length) {
                        let data = rows.map(row => {
                            let record = result.data.find(record => row.packetid === record.packetid);
                            if (record) {
                                return {
                                    ...row,
                                    carats: record['carats'] || row.carats,
                                    ppc: record['ppc'] || row.ppc
                                }
                            } else
                                return row;
                        })

                        setData(data);
                        setUploadLoader(false);
                        setSnackbarState(true, "Uploaded Successfully", "success");
                    } else {
                        console.error('CSV columns do not match table columns.');
                        setUploadLoader(false);
                        setSnackbarState(true, "CSV columns do not match table columns.", "error");
                    }
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    setUploadLoader(false);
                    setSnackbarState(true, `'Error parsing CSV:', ${error}`, "error");
                }
            });
        };

        reader.readAsText(file);
    };

    const handleClear = () => {
        const fileInput = document.getElementById('file-input');
        fileInput.value = null;
    }

    const rows = data;
    const columns = columnName.filter((iData) => !addPacketPermission ? iData.field !== "delete" : true);
    return (
        <div>

            {showPacketDetailModal ? (
                <PacketHistoryModal
                    show={showPacketDetailModal}
                    packetId={packetId}
                    kapanId={kapanId}
                    close={close}
                />
            ) : null}
            <Modal backdrop="static" show={Show} onHide={handleClose} enforceFocus={false}>
                <CommonSnackbar
                    show={showError}
                    message={
                        <Box>
                            Create Packet validation Failed
                            <br /> Please enter correct data
                        </Box>
                    }
                    variant={"error"}
                    autoHide={false}
                    handleClose={() => setShowError(false)}
                    title="Validation Failure"
                />
                <CommonSnackbar
                    show={showSnackbar}
                    message={snackbarMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" :
                        snackbarVariant === "error" ? "Failed" : "Warning"}
                    handleClose={() => setShowSnackbar(false)}
                />
                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;Packaging  (Kapan Id : {kapanId})
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {barcodeModal ?
                        (
                            <div id="barcodeData" name="barcodeData" style={{ display: "none" }}>
                                <PrintBarcode
                                    Show={true}
                                    handleClose={() => setBarcodeModal(false)}
                                    sourceData={data}
                                />
                            </div>
                        ) : (
                            <Box />
                        )}
                    <Box>
                        <Box style={{ display: 'flex', flexDirection: 'row', width: '100%', marginBottom: '8px', justifyContent: "space-between", }}>

                            {addPacketPermission ? (<Button variant="contained"
                                startIcon={<AddBoxOutlinedIcon size="15px" />}
                                onClick={addPacket}
                                disabled={fromKapan ? data.length >= kapanPcs : false}
                            >
                                Add Packet
                            </Button>) : null}

                            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <Box>
                                    {addPacketPermission ? (
                                        <Tooltip title="Download Sample Packet Upload">
                                            <a
                                                href="/demo_excel/SamplePacketUpload.csv"
                                                download="SamplePacketUpload.csv"
                                                style={{ color: "red", marginRight: "5px", marginLeft: "5px" }}
                                            >
                                                <BackupTable size={25} />
                                            </a>
                                        </Tooltip>
                                    ) : null}

                                    {addPacketPermission ? (
                                        <Tooltip title="Upload Packet">
                                            <LoadingButton
                                                color="info"
                                                variant="outlined"
                                                size="small"
                                                sx={{ ml: "3px" }}
                                                component="label"
                                                loading={uploadLoader}
                                                loadingPosition="start"
                                                startIcon={<UploadFileIcon size="large" />}
                                                disabled={rows.length === 0}
                                            >
                                                Upload
                                                <input
                                                    id="file-input"
                                                    hidden
                                                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                                    multiple
                                                    type="file"
                                                    onChange={(e) => handleUpload(e.target.files[0])}
                                                    onClick={(e) => handleClear()}
                                                />
                                            </LoadingButton>
                                        </Tooltip>
                                    ) : null}
                                </Box>

                                <Button variant="contained"
                                    startIcon={<IoMdBarcode size="15px" />}
                                    onClick={() => {
                                        setBarcodeModal(true); setTimeout(() => {
                                            var newWindow = window.open();
                                            var content = document.getElementById("barcodeData");
                                            newWindow.document.write(content.innerHTML);
                                            newWindow.print();
                                        }, 500);
                                    }}
                                >
                                    Print
                                </Button>
                            </Box>

                        </Box>
                        <Box
                            sx={{
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .MuiDataGrid-toolbarContainer": {
                                    padding: "4px 4px 0 4px !important"
                                },
                                height: "50vh",
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={true}
                                density="compact"
                                rowHeight={60}
                                loading={detailLoader}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    "& .lavenderCell": {
                                        color: "#080808",
                                        backgroundColor: "#D8BFD8",
                                    },
                                    "& .cursorNA": {
                                        color: "#080808",
                                        backgroundColor: "#D8BFD8",
                                        cursor: "not-allowed"
                                    },
                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeightBold,
                                }}
                                // onCellEditCommit={(data) => onCellEditCommit(data)}
                                rows={rows}
                                columns={columns}
                                getRowId={(row) => row.packetid}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns id, the other columns will remain visible
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                slots={{ toolbar: GridToolbar }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                processRowUpdate={(newR, oldRow) => processRowUpdate(newR, oldRow)}
                                onProcessRowUpdateError={handleProcessRowUpdateError}
                                apiRef={apiRef}
                                onCellClick={handleCellClick}
                                editMode="cell"
                            />
                        </Box>
                    </Box>
                </Modal.Body>
                <Modal.Footer sx={{ display: "flex", alignItems: "center", width: "100%", padding: "0 16px" }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", flexGrow: 1, textAlign: "left" }}>
                        Max Carat: {maxCarats}
                    </Typography>
                    <LoadingButton
                        loading={loading}
                        loadingPosition="start"
                        variant="contained"
                        component="label"
                        startIcon={<AddBoxOutlinedIcon size="large" />}
                        onClick={handleCreateLot}
                        disabled={data.length === 0}
                        sx={{ mr: "15px" }}
                    >
                        Save
                    </LoadingButton>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div >
    );
};

export default AddPackageDetail;
